import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../../components/ui/table';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Badge } from '../../components/ui/badge';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '../../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { CheckCircle, XCircle, DollarSign, Filter, Edit, Calculator } from 'lucide-react';
import { toast } from 'sonner';
import { mockAllTransactions, mockAllUsers } from '../../mockAdmin';
import { commissionTiers } from '../../mock';

const CommissionsManagement = () => {
  const navigate = useNavigate();
  const [transactions, setTransactions] = useState(mockAllTransactions);
  const [filteredTransactions, setFilteredTransactions] = useState(mockAllTransactions);
  const [statusFilter, setStatusFilter] = useState('all');
  const [selectedTransaction, setSelectedTransaction] = useState(null);
  const [isPayoutDialogOpen, setIsPayoutDialogOpen] = useState(false);
  const [isEditDialogOpen, setIsEditDialogOpen] = useState(false);
  const [selectedUser, setSelectedUser] = useState(null);
  const [payoutAmount, setPayoutAmount] = useState('');
  const [customAmount, setCustomAmount] = useState('');
  const [calculatedCommission, setCalculatedCommission] = useState(0);
  const [commissionRate, setCommissionRate] = useState(0);
  const [userTier, setUserTier] = useState(null);

  useEffect(() => {
    const isAdminAuth = localStorage.getItem('isAdminAuthenticated');
    if (!isAdminAuth) {
      navigate('/admin/login');
    }
  }, [navigate]);

  useEffect(() => {
    if (statusFilter === 'all') {
      setFilteredTransactions(transactions);
    } else {
      setFilteredTransactions(transactions.filter(t => t.status.toLowerCase() === statusFilter));
    }
  }, [statusFilter, transactions]);

  const handleApprove = (transaction) => {
    const updatedTransactions = transactions.map(t => 
      t.id === transaction.id ? { ...t, status: 'Confirmed' } : t
    );
    setTransactions(updatedTransactions);
    toast.success(`Transaction ${transaction.orderId} approved!`);
  };

  const handleReject = (transaction) => {
    const updatedTransactions = transactions.map(t => 
      t.id === transaction.id ? { ...t, status: 'Rejected' } : t
    );
    setTransactions(updatedTransactions);
    toast.error(`Transaction ${transaction.orderId} rejected!`);
  };

  const handleOpenPayoutDialog = (user) => {
    setSelectedUser(user);
    setPayoutAmount(user.balance.toString());
    setIsPayoutDialogOpen(true);
  };

  const handleProcessPayout = () => {
    const amount = parseFloat(payoutAmount);
    if (amount > 0 && amount <= selectedUser.balance) {
      const newTransaction = {
        id: `txn_payout_${Date.now()}`,
        userId: selectedUser.id,
        userName: `${selectedUser.firstName} ${selectedUser.lastName}`,
        date: new Date().toISOString().split('T')[0],
        type: 'Payout',
        orderId: '-',
        customerName: '-',
        amount: amount,
        commission: -amount,
        commissionRate: 0,
        status: 'Paid',
        product: `Withdrawal to ${selectedUser.payoutMethod}`
      };
      setTransactions([newTransaction, ...transactions]);
      toast.success(`Payout of ₹${amount.toLocaleString()} processed for ${selectedUser.firstName}!`);
      setIsPayoutDialogOpen(false);
    } else {
      toast.error('Invalid payout amount!');
    }
  };

  const handleOpenEditDialog = (transaction) => {
    setSelectedTransaction(transaction);
    setCustomAmount(transaction.amount.toString());
    
    // Find user to get tier info
    const user = mockAllUsers.find(u => `${u.firstName} ${u.lastName}` === transaction.userName);
    if (user) {
      const tier = commissionTiers.find(t => t.tier === user.currentTier);
      setUserTier(tier);
      setCommissionRate(tier?.rate || 0);
      
      // Calculate initial commission
      const commission = (transaction.amount * (tier?.rate || 0)) / 100;
      setCalculatedCommission(commission);
    }
    
    setIsEditDialogOpen(true);
  };

  const handleAmountChange = (value) => {
    setCustomAmount(value);
    const amount = parseFloat(value) || 0;
    
    if (userTier && amount > 0) {
      const commission = (amount * userTier.rate) / 100;
      setCalculatedCommission(commission);
    } else {
      setCalculatedCommission(0);
    }
  };

  const handleSaveTransaction = () => {
    if (!customAmount || parseFloat(customAmount) <= 0) {
      toast.error('Please enter a valid amount');
      return;
    }

    const updatedTransactions = transactions.map(t => 
      t.id === selectedTransaction.id 
        ? { 
            ...t, 
            amount: parseFloat(customAmount),
            commission: calculatedCommission,
            commissionRate: commissionRate
          } 
        : t
    );
    
    setTransactions(updatedTransactions);
    toast.success('Transaction updated successfully!');
    setIsEditDialogOpen(false);
  };

  const getStatusBadge = (status) => {
    const statusConfig = {
      'Confirmed': { className: 'bg-green-100 text-green-800 hover:bg-green-100' },
      'Pending': { className: 'bg-orange-100 text-orange-800 hover:bg-orange-100' },
      'Rejected': { className: 'bg-red-100 text-red-800 hover:bg-red-100' },
      'Paid': { className: 'bg-blue-100 text-blue-800 hover:bg-blue-100' }
    };
    const config = statusConfig[status] || statusConfig['Pending'];
    return (
      <Badge className={config.className}>
        {status}
      </Badge>
    );
  };

  const pendingCount = transactions.filter(t => t.status === 'Pending').length;
  const totalPending = transactions
    .filter(t => t.status === 'Pending' && t.commission > 0)
    .reduce((sum, t) => sum + t.commission, 0);

  return (
    <div className="min-h-screen py-12">
      <div className="max-w-7xl mx-auto px-6">
        {/* Summary Cards */}
        <div className="grid md:grid-cols-3 gap-6 mb-8">
          <Card className="border-3 border-orange-300 shadow-lg bg-white/90 backdrop-blur-sm">
            <CardHeader>
              <CardTitle className="text-lg">Pending Approvals</CardTitle>
            </CardHeader>
            <CardContent>
              <p className="text-4xl font-bold text-orange-600">{pendingCount}</p>
              <p className="text-sm text-gray-600 mt-2">Transactions awaiting review</p>
            </CardContent>
          </Card>

          <Card className="border-3 border-red-300 shadow-lg bg-white/90 backdrop-blur-sm">
            <CardHeader>
              <CardTitle className="text-lg">Pending Amount</CardTitle>
            </CardHeader>
            <CardContent>
              <p className="text-4xl font-bold text-red-600">₹{totalPending.toLocaleString()}</p>
              <p className="text-sm text-gray-600 mt-2">Total pending commissions</p>
            </CardContent>
          </Card>

          <Card className="border-3 border-green-300 shadow-lg bg-white/90 backdrop-blur-sm">
            <CardHeader>
              <CardTitle className="text-lg">Process Payout</CardTitle>
            </CardHeader>
            <CardContent>
              <Select onValueChange={(userId) => {
                const user = mockAllUsers.find(u => u.id === userId);
                if (user) handleOpenPayoutDialog(user);
              }}>
                <SelectTrigger className="h-12">
                  <SelectValue placeholder="Select User" />
                </SelectTrigger>
                <SelectContent>
                  {mockAllUsers.filter(u => u.balance >= 1000).map(user => (
                    <SelectItem key={user.id} value={user.id}>
                      {user.firstName} {user.lastName} (₹{user.balance.toLocaleString()})
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </CardContent>
          </Card>
        </div>

        {/* Filters */}
        <Card className="border-3 border-yellow-300 shadow-lg bg-white/90 backdrop-blur-sm mb-6">
          <CardContent className="p-6">
            <div className="flex items-center gap-4">
              <Filter className="h-5 w-5 text-gray-600" />
              <Label className="font-semibold">Filter by Status:</Label>
              <Select value={statusFilter} onValueChange={setStatusFilter}>
                <SelectTrigger className="w-48">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Transactions</SelectItem>
                  <SelectItem value="pending">Pending</SelectItem>
                  <SelectItem value="confirmed">Confirmed</SelectItem>
                  <SelectItem value="rejected">Rejected</SelectItem>
                  <SelectItem value="paid">Paid</SelectItem>
                </SelectContent>
              </Select>
              <span className="text-sm text-gray-600">Showing {filteredTransactions.length} transactions</span>
            </div>
          </CardContent>
        </Card>

        {/* Transactions Table */}
        <Card className="border-3 border-red-300 shadow-lg bg-white/90 backdrop-blur-sm">
          <CardHeader>
            <CardTitle>All Transactions</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Date</TableHead>
                    <TableHead>User</TableHead>
                    <TableHead>Type</TableHead>
                    <TableHead>Order ID</TableHead>
                    <TableHead>Customer</TableHead>
                    <TableHead>Product</TableHead>
                    <TableHead className="text-right">Amount</TableHead>
                    <TableHead className="text-right">Commission</TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead className="text-center">Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {filteredTransactions && filteredTransactions.length > 0 ? filteredTransactions.map((transaction) => (
                    <TableRow key={transaction.id} className="hover:bg-gray-50">
                      <TableCell className="font-medium">
                        {new Date(transaction.date).toLocaleDateString('en-IN', {
                          day: '2-digit',
                          month: 'short',
                          year: 'numeric'
                        })}
                      </TableCell>
                      <TableCell className="font-medium">{transaction.userName}</TableCell>
                      <TableCell>
                        <Badge variant="outline" className="font-medium">
                          {transaction.type}
                        </Badge>
                      </TableCell>
                      <TableCell className="font-mono text-sm">{transaction.orderId}</TableCell>
                      <TableCell>{transaction.customerName}</TableCell>
                      <TableCell className="max-w-xs truncate">{transaction.product}</TableCell>
                      <TableCell className="text-right font-semibold">
                        ₹{transaction.amount.toLocaleString()}
                      </TableCell>
                      <TableCell className="text-right font-semibold">
                        <span className={transaction.commission >= 0 ? 'text-green-600' : 'text-red-600'}>
                          {transaction.commission >= 0 ? '+' : ''}₹{transaction.commission.toLocaleString()}
                        </span>
                        {transaction.commissionRate > 0 && (
                          <span className="text-xs text-gray-500 ml-1">({transaction.commissionRate}%)</span>
                        )}
                      </TableCell>
                      <TableCell>{getStatusBadge(transaction.status)}</TableCell>
                      <TableCell>
                        <div className="flex gap-2 justify-center">
                          {transaction.status === 'Pending' && transaction.type === 'Sale' && (
                            <>
                              <Button
                                size="sm"
                                className="bg-green-600 hover:bg-green-700 text-white"
                                onClick={() => handleApprove(transaction)}
                              >
                                <CheckCircle className="h-4 w-4" />
                              </Button>
                              <Button
                                size="sm"
                                className="bg-red-600 hover:bg-red-700 text-white"
                                onClick={() => handleReject(transaction)}
                              >
                                <XCircle className="h-4 w-4" />
                              </Button>
                            </>
                          )}
                          {transaction.type === 'Sale' && (
                            <Button
                              size="sm"
                              className="bg-blue-600 hover:bg-blue-700 text-white"
                              onClick={() => handleOpenEditDialog(transaction)}
                            >
                              <Edit className="h-4 w-4" />
                            </Button>
                          )}
                          {transaction.status !== 'Pending' && transaction.type !== 'Sale' && (
                            <span className="text-gray-400 text-sm">-</span>
                          )}
                        </div>
                      </TableCell>
                    </TableRow>
                  )) : (
                    <TableRow>
                      <TableCell colSpan={7} className="text-center py-8 text-gray-500">
                        {filteredTransactions ? 'No transactions found' : 'Loading transactions...'}
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </div>
          </CardContent>
        </Card>

        {/* Payout Dialog */}
        <Dialog open={isPayoutDialogOpen} onOpenChange={setIsPayoutDialogOpen}>
          <DialogContent className="bg-white">
            <DialogHeader>
              <DialogTitle>Process Payout</DialogTitle>
              <DialogDescription>
                Process withdrawal for user
              </DialogDescription>
            </DialogHeader>
            {selectedUser && (
              <div className="space-y-4">
                <div className="bg-gray-50 p-4 rounded-lg">
                  <p className="text-sm text-gray-600">User</p>
                  <p className="text-lg font-bold">{selectedUser.firstName} {selectedUser.lastName}</p>
                  <p className="text-sm text-gray-600 mt-2">Available Balance</p>
                  <p className="text-2xl font-bold text-green-600">₹{selectedUser.balance.toLocaleString()}</p>
                </div>
                <div className="space-y-2">
                  <Label>Payout Amount</Label>
                  <Input
                    type="number"
                    placeholder="Enter amount"
                    value={payoutAmount}
                    onChange={(e) => setPayoutAmount(e.target.value)}
                    max={selectedUser.balance}
                  />
                  <p className="text-xs text-gray-500">Maximum: ₹{selectedUser.balance.toLocaleString()}</p>
                </div>
                <div className="space-y-2">
                  <Label>Payout Method</Label>
                  <Input value={selectedUser.payoutMethod} disabled />
                </div>
                <div className="flex gap-3 pt-4">
                  <Button
                    className="flex-1 text-white border-2 border-green-400 bg-green-600 hover:bg-green-700"
                    onClick={handleProcessPayout}
                  >
                    <DollarSign className="h-4 w-4 mr-2" />
                    Process Payout
                  </Button>
                  <Button
                    variant="outline"
                    className="flex-1"
                    onClick={() => setIsPayoutDialogOpen(false)}
                  >
                    Cancel
                  </Button>
                </div>
              </div>
            )}
          </DialogContent>
        </Dialog>

        {/* Edit Transaction Dialog */}
        <Dialog open={isEditDialogOpen} onOpenChange={setIsEditDialogOpen}>
          <DialogContent className="bg-white max-w-2xl">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <Calculator className="h-5 w-5 text-blue-600" />
                Edit Transaction & Calculate Commission
              </DialogTitle>
              <DialogDescription>
                Update the transaction amount and view stage-wise commission calculation
              </DialogDescription>
            </DialogHeader>
            {selectedTransaction && (
              <div className="space-y-6">
                {/* Transaction Details */}
                <div className="bg-gray-50 p-4 rounded-lg space-y-2">
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <p className="text-sm text-gray-600">Order ID</p>
                      <p className="font-semibold">{selectedTransaction.orderId}</p>
                    </div>
                    <div>
                      <p className="text-sm text-gray-600">User</p>
                      <p className="font-semibold">{selectedTransaction.userName}</p>
                    </div>
                    <div>
                      <p className="text-sm text-gray-600">Customer</p>
                      <p className="font-semibold">{selectedTransaction.customerName}</p>
                    </div>
                    <div>
                      <p className="text-sm text-gray-600">Product</p>
                      <p className="font-semibold">{selectedTransaction.product}</p>
                    </div>
                  </div>
                </div>

                {/* Amount Input */}
                <div className="space-y-2">
                  <Label className="text-base font-semibold">Custom Price Amount</Label>
                  <Input
                    type="number"
                    placeholder="Enter custom amount"
                    value={customAmount}
                    onChange={(e) => handleAmountChange(e.target.value)}
                    className="h-12 text-lg font-semibold"
                  />
                  <p className="text-xs text-gray-500">Enter the transaction amount to calculate commission</p>
                </div>

                {/* Commission Calculation */}
                {userTier && (
                  <div className="bg-gradient-to-br from-blue-50 to-indigo-50 p-6 rounded-lg border-2 border-blue-200">
                    <div className="flex items-center justify-between mb-4">
                      <h3 className="text-lg font-bold text-gray-800">Commission Breakdown</h3>
                      <Badge className="bg-blue-600 text-white text-sm px-3 py-1">
                        {userTier.name}
                      </Badge>
                    </div>

                    <div className="space-y-3">
                      <div className="flex justify-between items-center">
                        <span className="text-gray-700">Transaction Amount:</span>
                        <span className="text-xl font-bold text-gray-900">₹{parseFloat(customAmount || 0).toLocaleString()}</span>
                      </div>
                      
                      <div className="flex justify-between items-center">
                        <span className="text-gray-700">User Tier:</span>
                        <span className="font-semibold" style={{ color: userTier.color }}>
                          {userTier.name}
                        </span>
                      </div>
                      
                      <div className="flex justify-between items-center">
                        <span className="text-gray-700">Sales Range:</span>
                        <span className="text-sm text-gray-600">{userTier.salesRange}</span>
                      </div>
                      
                      <div className="flex justify-between items-center py-2 border-t border-blue-200">
                        <span className="text-gray-700 font-semibold">Commission Rate:</span>
                        <span className="text-2xl font-bold text-blue-600">{commissionRate}%</span>
                      </div>
                      
                      <div className="flex justify-between items-center py-3 bg-white rounded-lg px-4 border-2 border-green-400">
                        <span className="text-gray-700 font-bold">Commission Amount:</span>
                        <span className="text-3xl font-bold text-green-600">
                          ₹{calculatedCommission.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                        </span>
                      </div>

                      <div className="mt-4 p-3 bg-blue-100 rounded-lg">
                        <p className="text-xs text-gray-700">
                          <strong>Calculation:</strong> ₹{parseFloat(customAmount || 0).toLocaleString()} × {commissionRate}% = ₹{calculatedCommission.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                        </p>
                      </div>
                    </div>
                  </div>
                )}

                {/* Action Buttons */}
                <div className="flex gap-3 pt-4">
                  <Button
                    className="flex-1 text-white border-2 border-blue-400 bg-blue-600 hover:bg-blue-700 h-12"
                    onClick={handleSaveTransaction}
                  >
                    <CheckCircle className="h-5 w-5 mr-2" />
                    Save Changes
                  </Button>
                  <Button
                    variant="outline"
                    className="flex-1 h-12"
                    onClick={() => setIsEditDialogOpen(false)}
                  >
                    Cancel
                  </Button>
                </div>
              </div>
            )}
          </DialogContent>
        </Dialog>
      </div>
    </div>
  );
};

export default CommissionsManagement;
